import { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react'
import { publicAPI } from '../services/api'
import { useAuth } from './AuthContext'

interface YearContextType {
  selectedYear: '2025' | '2026'
  setSelectedYear: (year: '2025' | '2026') => void
  getYearDateRange: () => { startDate: Date; endDate: Date }
  /** The most recent SVV registration date as a Date (null while the API fetch is in-flight). */
  maxDataDate: Date | null
  /** Raw SVV latest_data_date as YYYY-MM-DD string, capped at today (null while loading). */
  maxDataDateStr: string | null
  /** Raw BrregLoan latest_data_date as YYYY-MM-DD string, capped at today (null while loading). */
  maxLoanDateStr: string | null
}

const YearContext = createContext<YearContextType | undefined>(undefined)

export function YearProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [selectedYear, setSelectedYear] = useState<'2025' | '2026'>('2026')
  const [maxDataDate, setMaxDataDate] = useState<Date | null>(null)
  const [maxDataDateStr, setMaxDataDateStr] = useState<string | null>(null)
  const [maxLoanDateStr, setMaxLoanDateStr] = useState<string | null>(null)

  // Fetch data-freshness once authenticated; extract all date fields from single response.
  useEffect(() => {
    if (!user) return
    publicAPI.getDataFreshness()
      .then((response: any) => {
        const sources: any[] = response.data?.data_sources ?? []
        const todayStr = new Date().toISOString().substring(0, 10)

        // SVV source — cutoff date + raw date string
        const svvSource = sources.find((s: any) =>
          typeof s.source_name === 'string' && s.source_name.includes('SVV')
        )
        if (svvSource?.latest_data_date) {
          const latestDate = new Date(svvSource.latest_data_date)
          if (!isNaN(latestDate.getTime())) {
            latestDate.setHours(12, 0, 0, 0)
            setMaxDataDate(latestDate)
          }
          const raw = svvSource.latest_data_date as string
          const dateStr = raw.substring(0, 10)
          setMaxDataDateStr(dateStr <= todayStr ? dateStr : todayStr)
        }

        // Loan source — raw date string
        const loanSource = sources.find((s: any) =>
          typeof s.source_name === 'string' &&
          (s.source_name.includes('Lånedekning') || s.source_name.includes('Loan Coverage'))
        )
        if (loanSource?.latest_data_date) {
          const raw = loanSource.latest_data_date as string
          const dateStr = raw.substring(0, 10)
          setMaxLoanDateStr(dateStr <= todayStr ? dateStr : todayStr)
        }
      })
      .catch(() => {
        // Silently fall back — dates stay null
      })
  }, [user])

  const getYearDateRange = () => {
    if (selectedYear === '2025') {
      return {
        startDate: new Date(2025, 0, 1, 12, 0, 0),
        endDate: new Date(2025, 11, 31, 12, 0, 0)
      }
    } else {
      // 2026: Jan 1 → most recent SVV registration date (or today while fetch is in-flight)
      return {
        startDate: new Date(2026, 0, 1, 12, 0, 0),
        endDate: maxDataDate ?? new Date()
      }
    }
  }

  return (
    <YearContext.Provider value={{ selectedYear, setSelectedYear, getYearDateRange, maxDataDate, maxDataDateStr, maxLoanDateStr }}>
      {children}
    </YearContext.Provider>
  )
}

export function useYear() {
  const context = useContext(YearContext)
  if (context === undefined) {
    throw new Error('useYear must be used within a YearProvider')
  }
  return context
}

// Custom hook for pages with date pickers. Persists startDate/endDate to
// sessionStorage under a SHARED key so the last queried range carries from
// page to page within the same tab.
//
// The optional `pageKey` parameter is accepted for backwards compatibility
// but is no longer used for storage scoping — dates are global across pages.
const SHARED_START_KEY = 'bp:filter:startDate'
const SHARED_END_KEY = 'bp:filter:endDate'

function readStoredDate(storageKey: string): Date | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(storageKey)
    if (!raw) return null
    const d = new Date(JSON.parse(raw))
    return isNaN(d.getTime()) ? null : d
  } catch {
    return null
  }
}

// The period choice the pages share, as written by usePersistedState
// ('bp:page:' namespace). Absent means the default, year-to-date.
function readStoredOutputMode(): string {
  if (typeof window === 'undefined') return 'year_to_date'
  try {
    const raw = window.sessionStorage.getItem('bp:page:bp:filter:outputMode')
    return raw === null ? 'year_to_date' : JSON.parse(raw)
  } catch {
    return 'year_to_date'
  }
}

function writeStoredDate(storageKey: string, value: Date): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(value.toISOString()))
  } catch {
    // ignore quota / privacy-mode errors
  }
}

export function useYearDates(_pageKey?: string) {
  const { selectedYear, getYearDateRange, maxDataDate } = useYear()
  const [startDate, setStartDate] = useState<Date>(
    () => readStoredDate(SHARED_START_KEY) ?? getYearDateRange().startDate
  )
  const [endDate, setEndDate] = useState<Date>(
    () => readStoredDate(SHARED_END_KEY) ?? getYearDateRange().endDate
  )

  // Re-sync dates when the year changes OR when the max data date resolves.
  // On the very first render, skip the sync if we restored persisted values
  // (otherwise we'd immediately overwrite them with year defaults).
  const isFirstRunRef = useRef(true)
  const restoredRef = useRef(false)
  const prevYearRef = useRef(selectedYear)
  useEffect(() => {
    if (isFirstRunRef.current) {
      isFirstRunRef.current = false
      const hadStoredStart = readStoredDate(SHARED_START_KEY) !== null
      const hadStoredEnd = readStoredDate(SHARED_END_KEY) !== null
      restoredRef.current = hadStoredStart && hadStoredEnd
      if (restoredRef.current) return
    }
    const yearChanged = prevYearRef.current !== selectedYear
    prevYearRef.current = selectedYear
    // After a reload the max data date lands after the restore. Only a
    // year-to-date range depends on it; a restored "Annen periode" or
    // "Forrige måned" must survive it (bilpriser-backend#307).
    if (!yearChanged && restoredRef.current && readStoredOutputMode() !== 'year_to_date') return
    const { startDate: newStart, endDate: newEnd } = getYearDateRange()
    setStartDate(newStart)
    setEndDate(newEnd)
  }, [selectedYear, maxDataDate])

  useEffect(() => {
    writeStoredDate(SHARED_START_KEY, startDate)
  }, [startDate])
  useEffect(() => {
    writeStoredDate(SHARED_END_KEY, endDate)
  }, [endDate])

  return { startDate, setStartDate, endDate, setEndDate, selectedYear, maxSvvDate: maxDataDate }
}
